import { type BaseError, createAppError } from '@bangle.io/base-utils';
import type { EditorSaveStatus } from '@bangle.io/context';
import { writeLocalUnsavedDrafts } from '@bangle.io/service-core';

export type { EditorSaveStatus };

const CLEAN_SAVE_STATUS: EditorSaveStatus = { status: 'clean' };

/**
 * Backoff delays for automatic retries after a durable write fails. Exhausting
 * the list leaves the entry in `failed` for an explicit user retry.
 */
export const EDITOR_SAVE_AUTO_RETRY_DELAYS_MS = [500, 1_500, 4_000, 10_000];

/**
 * Quiet period before a durable write starts. Coalesces keystrokes so the
 * save pill does not flip Saved↔Saving between every character. Callers that
 * need an immediate write (blur, navigate, pagehide) should call
 * {@link EditorSaveQueue.flushDebouncedSaves}.
 */
export const EDITOR_SAVE_DEBOUNCE_MS = 200;

export type EditorSaveQueueOptions = {
  /** Defaults to 0 so unit tests stay synchronous; production uses 200ms. */
  debounceMs?: number;
};

type SaveWriter = (wsPath: string, doc: string) => Promise<void>;
type ErrorHandler = (error: BaseError) => void;

export type EditorSaveQueueHooks = {
  /** Fired whenever a note body is queued (including coalesced edits). */
  onQueued?: (wsPath: string, doc: string) => void;
  /** Fired when a note's queue drains to a confirmed clean write. */
  onClean?: (wsPath: string) => void;
};

type SaveTask = {
  doc: string;
};

type EditorSaveSession = {
  emitAppError: ErrorHandler;
  hooks: EditorSaveQueueHooks;
  writeDoc: SaveWriter;
};

type SaveEntry = {
  active: boolean;
  activeTask?: SaveTask;
  blockingFlush?: Promise<void>;
  debounceTimer?: ReturnType<typeof setTimeout>;
  failedTask?: SaveTask;
  flushPromise?: Promise<void>;
  pendingTask?: SaveTask;
  retired: boolean;
  retryAttempt: number;
  retryTimer?: ReturnType<typeof setTimeout>;
  status: EditorSaveStatus;
  wsPath: string;
};

type SaveStatusListener = () => void;
type SaveStatusSubscription = {
  listener: SaveStatusListener;
  wsPath?: string;
};

/**
 * Browser-root state shared by each editor service graph created in this tab.
 * Queued documents can outlive a UI reload, while the current session always
 * points at the newest graph's writer and error boundary.
 */
export type EditorSaveCoordinator = {
  currentSession?: EditorSaveSession;
  entries: Map<string, SaveEntry>;
  hasPendingOrFailedSave(wsPath?: string): boolean;
  /**
   * Latest not-yet-confirmed Markdown bodies, for synchronous pagehide backup.
   */
  latestBodies: Map<string, string>;
  /**
   * `pendingCount:failedCount` signature so global subscribers are notified
   * when pending becomes failed (dirty boolean alone would miss that).
   */
  lastGlobalNotifySignature: string;
  subscriptions: Set<SaveStatusSubscription>;
};

function globalNotifySignature(entries: Map<string, SaveEntry>): string {
  let pendingCount = 0;
  let failedCount = 0;
  for (const entry of entries.values()) {
    if (entry.status.status === 'pending') {
      pendingCount += 1;
    } else if (entry.status.status === 'failed') {
      failedCount += 1;
    }
  }
  return `${pendingCount}:${failedCount}`;
}

export function createEditorSaveCoordinator(): EditorSaveCoordinator {
  const entries = new Map<string, SaveEntry>();
  return {
    entries,
    hasPendingOrFailedSave: (wsPath) => {
      if (wsPath !== undefined) {
        const entry = entries.get(wsPath);
        return entry !== undefined && entry.status.status !== 'clean';
      }
      for (const entry of entries.values()) {
        if (entry.status.status !== 'clean') {
          return true;
        }
      }
      return false;
    },
    latestBodies: new Map(),
    lastGlobalNotifySignature: '0:0',
    subscriptions: new Set(),
  };
}

/** Flushes in-memory pending/failed bodies to localStorage for crash recovery. */
export function flushUnsavedDraftsToLocalStorage(
  coordinator: EditorSaveCoordinator,
): void {
  writeLocalUnsavedDrafts(coordinator.latestBodies);
}

export class EditorSaveQueue {
  private readonly debounceMs: number;

  constructor(
    writeDoc: SaveWriter,
    emitAppError: ErrorHandler,
    private coordinator: EditorSaveCoordinator = createEditorSaveCoordinator(),
    hooks: EditorSaveQueueHooks = {},
    options: EditorSaveQueueOptions = {},
  ) {
    this.coordinator.currentSession = { writeDoc, emitAppError, hooks };
    this.debounceMs = options.debounceMs ?? 0;
  }

  enqueue(wsPath: string, doc: string): void {
    const entry = this.getEntry(wsPath);
    this.clearAutoRetry(entry);
    entry.retryAttempt = 0;
    entry.failedTask = undefined;
    entry.pendingTask = { doc };
    entry.status = { status: 'pending' };
    this.coordinator.latestBodies.set(wsPath, doc);
    this.coordinator.currentSession?.hooks.onQueued?.(wsPath, doc);
    this.notifySaveStatusChanged(wsPath);

    if (entry.active) {
      return;
    }

    this.scheduleFlush(entry);
  }

  /**
   * Clears debounce timers and starts any waiting flush immediately. Used by
   * navigation / pagehide drain paths so pending edits are not held for the
   * quiet period.
   */
  flushDebouncedSaves(wsPath?: string): void {
    if (wsPath !== undefined) {
      const entry = this.coordinator.entries.get(wsPath);
      if (entry && !entry.active && entry.pendingTask) {
        this.clearDebounce(entry);
        this.startFlush(entry);
      }
      return;
    }

    for (const entry of this.coordinator.entries.values()) {
      if (!entry.active && entry.pendingTask) {
        this.clearDebounce(entry);
        this.startFlush(entry);
      }
    }
  }

  getStatus(wsPath: string): EditorSaveStatus {
    return this.coordinator.entries.get(wsPath)?.status ?? CLEAN_SAVE_STATUS;
  }

  listFailedSaves(): ReadonlyArray<{ error: Error; wsPath: string }> {
    const failed: Array<{ error: Error; wsPath: string }> = [];
    for (const entry of this.coordinator.entries.values()) {
      if (entry.status.status === 'failed') {
        failed.push({ error: entry.status.error, wsPath: entry.wsPath });
      }
    }
    return failed;
  }

  hasPendingOrFailed(wsPath?: string): boolean {
    return this.coordinator.hasPendingOrFailedSave(wsPath);
  }

  subscribe(listener: SaveStatusListener, wsPath?: string): () => void {
    const subscription = { listener, wsPath };
    this.coordinator.subscriptions.add(subscription);
    return () => {
      this.coordinator.subscriptions.delete(subscription);
    };
  }

  /**
   * Retargets queued work after a durable file rename. An in-flight write that
   * loses the race with the rename is retried at the destination, while a
   * newer pending edit continues to win through the queue's normal coalescing.
   */
  relocate(oldWsPath: string, newWsPath: string): void {
    if (oldWsPath === newWsPath) {
      return;
    }

    const entry = this.coordinator.entries.get(oldWsPath);
    if (!entry) {
      return;
    }
    const destinationEntry = this.coordinator.entries.get(newWsPath);
    if (destinationEntry && destinationEntry !== entry) {
      // The durable rename makes the source entry authoritative. Retire any
      // stale destination producer, then replay the latest source task after
      // its in-flight write settles so that it cannot overwrite the rename.
      this.clearAutoRetry(destinationEntry);
      destinationEntry.retired = true;
      destinationEntry.pendingTask = undefined;
      destinationEntry.failedTask = undefined;
      destinationEntry.status = { status: 'clean' };
      this.coordinator.latestBodies.delete(destinationEntry.wsPath);
      if (destinationEntry.flushPromise) {
        const destinationFlush = destinationEntry.flushPromise.catch(() => {});
        entry.blockingFlush = entry.blockingFlush
          ? Promise.allSettled([entry.blockingFlush, destinationFlush]).then(
              () => {},
            )
          : destinationFlush;
      }
      if (entry.activeTask) {
        entry.pendingTask ??= entry.activeTask;
      }
      if (this.coordinator.entries.get(newWsPath) === destinationEntry) {
        this.coordinator.entries.delete(newWsPath);
      }
    }

    this.coordinator.entries.delete(oldWsPath);
    const retainedBody = this.coordinator.latestBodies.get(oldWsPath);
    this.coordinator.latestBodies.delete(oldWsPath);
    entry.wsPath = newWsPath;
    this.coordinator.entries.set(newWsPath, entry);
    if (retainedBody !== undefined) {
      this.coordinator.latestBodies.set(newWsPath, retainedBody);
    }

    if (!entry.active && entry.failedTask) {
      entry.pendingTask = entry.failedTask;
      entry.failedTask = undefined;
      entry.status = { status: 'pending' };
    }
    if (!entry.active && entry.pendingTask) {
      this.clearDebounce(entry);
      this.startFlush(entry);
    }

    this.notifySaveStatusChanged(oldWsPath);
    this.notifySaveStatusChanged(newWsPath);
  }

  /**
   * Drop queued/failed work for a path that was deleted. Retires any in-flight
   * producer so a late write cannot recreate the note after purge.
   */
  discard(wsPath: string): void {
    const entry = this.coordinator.entries.get(wsPath);
    if (!entry) {
      this.coordinator.latestBodies.delete(wsPath);
      return;
    }
    this.clearAutoRetry(entry);
    this.clearDebounce(entry);
    entry.retired = true;
    entry.pendingTask = undefined;
    entry.failedTask = undefined;
    entry.status = { status: 'clean' };
    this.coordinator.latestBodies.delete(wsPath);
    if (this.coordinator.entries.get(wsPath) === entry) {
      this.coordinator.entries.delete(wsPath);
    }
    this.notifySaveStatusChanged(wsPath);
  }

  retryFailed(wsPath?: string): boolean {
    if (wsPath === undefined) {
      let retried = false;
      for (const path of [...this.coordinator.entries.keys()]) {
        retried = this.retryFailed(path) || retried;
      }
      return retried;
    }

    const entry = this.coordinator.entries.get(wsPath);
    if (!entry || entry.active || !entry.failedTask) {
      return false;
    }

    this.clearAutoRetry(entry);
    this.clearDebounce(entry);
    entry.pendingTask = entry.failedTask;
    entry.failedTask = undefined;
    entry.status = { status: 'pending' };
    this.notifySaveStatusChanged(wsPath);
    this.startFlush(entry);

    return true;
  }

  private getEntry(wsPath: string): SaveEntry {
    const existing = this.coordinator.entries.get(wsPath);
    if (existing) {
      return existing;
    }

    const entry: SaveEntry = {
      active: false,
      retired: false,
      retryAttempt: 0,
      status: { status: 'clean' },
      wsPath,
    };
    this.coordinator.entries.set(wsPath, entry);
    return entry;
  }

  private clearDebounce(entry: SaveEntry): void {
    if (entry.debounceTimer !== undefined) {
      clearTimeout(entry.debounceTimer);
      entry.debounceTimer = undefined;
    }
  }

  private scheduleFlush(entry: SaveEntry): void {
    this.clearDebounce(entry);
    if (this.debounceMs <= 0) {
      this.startFlush(entry);
      return;
    }
    const wsPath = entry.wsPath;
    entry.debounceTimer = setTimeout(() => {
      entry.debounceTimer = undefined;
      if (
        this.coordinator.entries.get(wsPath) === entry &&
        !entry.active &&
        entry.pendingTask
      ) {
        this.startFlush(entry);
      }
    }, this.debounceMs);
  }

  private clearAutoRetry(entry: SaveEntry): void {
    if (entry.retryTimer !== undefined) {
      clearTimeout(entry.retryTimer);
      entry.retryTimer = undefined;
    }
  }

  private scheduleAutoRetry(entry: SaveEntry): void {
    this.clearAutoRetry(entry);
    const delay = EDITOR_SAVE_AUTO_RETRY_DELAYS_MS[entry.retryAttempt];
    if (delay === undefined) {
      return;
    }
    entry.retryAttempt += 1;
    const wsPath = entry.wsPath;
    entry.retryTimer = setTimeout(() => {
      entry.retryTimer = undefined;
      if (
        this.coordinator.entries.get(wsPath) === entry &&
        entry.status.status === 'failed' &&
        entry.failedTask &&
        !entry.active
      ) {
        this.retryFailed(wsPath);
      }
    }, delay);
  }

  private markClean(entry: SaveEntry): void {
    this.clearAutoRetry(entry);
    this.clearDebounce(entry);
    entry.retryAttempt = 0;
    entry.failedTask = undefined;
    entry.pendingTask = undefined;
    entry.status = { status: 'clean' };
    this.coordinator.latestBodies.delete(entry.wsPath);
    this.coordinator.currentSession?.hooks.onClean?.(entry.wsPath);
    this.notifySaveStatusChanged(entry.wsPath);
    if (this.coordinator.entries.get(entry.wsPath) === entry) {
      this.coordinator.entries.delete(entry.wsPath);
    }
  }

  private async flush(entry: SaveEntry): Promise<void> {
    while (entry.pendingTask !== undefined) {
      const task = entry.pendingTask;
      entry.pendingTask = undefined;
      const blockingFlush = entry.blockingFlush;
      entry.blockingFlush = undefined;
      if (blockingFlush) {
        await blockingFlush;
      }
      if (entry.retired) {
        break;
      }

      const writeWsPath = entry.wsPath;
      entry.activeTask = task;

      try {
        const session = this.coordinator.currentSession;
        if (!session) {
          throw new Error('Editor save coordinator has no active session');
        }
        await session.writeDoc(writeWsPath, task.doc);
      } catch (cause) {
        if (entry.retired) {
          continue;
        }
        if (writeWsPath !== entry.wsPath) {
          // A durable rename overtook this write. Retry it at the destination
          // unless a newer edit is already pending there.
          entry.pendingTask ??= task;
          entry.failedTask = undefined;
          entry.status = { status: 'pending' };
          this.notifySaveStatusChanged(entry.wsPath);
          continue;
        }

        const error = cause instanceof Error ? cause : new Error(String(cause));
        if (entry.pendingTask === undefined) {
          entry.failedTask = task;
          entry.status = { error, status: 'failed' };
          this.coordinator.latestBodies.set(entry.wsPath, task.doc);
          this.notifySaveStatusChanged(entry.wsPath);
          this.coordinator.currentSession?.emitAppError(
            createAppError('error::editor:save-failed', 'Unable to save note', {
              error,
              wsPath: entry.wsPath,
            }),
          );
          this.scheduleAutoRetry(entry);
        } else {
          entry.failedTask = undefined;
          entry.status = { status: 'pending' };
          this.notifySaveStatusChanged(entry.wsPath);
        }
      } finally {
        entry.activeTask = undefined;
      }
    }

    entry.active = false;
    if (entry.retired) {
      this.clearAutoRetry(entry);
      entry.failedTask = undefined;
      entry.pendingTask = undefined;
      entry.status = { status: 'clean' };
      this.coordinator.latestBodies.delete(entry.wsPath);
      return;
    }

    if (entry.status.status === 'pending') {
      this.markClean(entry);
      return;
    }

    if (entry.pendingTask !== undefined) {
      this.startFlush(entry);
    }
  }

  private startFlush(entry: SaveEntry): void {
    entry.active = true;
    const flushPromise = this.flush(entry);
    entry.flushPromise = flushPromise;
    void flushPromise.then(
      () => {
        if (entry.flushPromise === flushPromise) {
          entry.flushPromise = undefined;
        }
      },
      () => {
        if (entry.flushPromise === flushPromise) {
          entry.flushPromise = undefined;
        }
      },
    );
  }

  private notifySaveStatusChanged(changedWsPath: string): void {
    const signature = globalNotifySignature(this.coordinator.entries);
    const globalStatusChanged =
      signature !== this.coordinator.lastGlobalNotifySignature;
    this.coordinator.lastGlobalNotifySignature = signature;

    for (const subscription of this.coordinator.subscriptions) {
      if (
        subscription.wsPath === changedWsPath ||
        (subscription.wsPath === undefined && globalStatusChanged)
      ) {
        subscription.listener();
      }
    }
  }
}
